import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { GlossaryEntryDTO } from '@tc/contracts';
import { api, ApiError } from '../api.ts';
import { useT } from '../i18n.ts';
import { GLOSSARY_STARTER, STARTER_MARK } from '../glossaryStarter.ts';

const COLS = [
  { locale: 'sv-SE', label: 'Svenska' },
  { locale: 'zh-Hans', label: '简体中文' },
  { locale: 'en', label: 'English' },
] as const;

interface Draft {
  terms: Record<string, string>;
  note: string;
  keepOriginal: boolean;
}

const empty = (): Draft => ({ terms: { 'sv-SE': '', 'zh-Hans': '', en: '' }, note: '', keepOriginal: false });
const split = (s: string) => s.split(';').map((x) => x.trim()).filter(Boolean);
const join = (a?: string[]) => (a ?? []).join('; ');

function toBody(d: Draft) {
  const terms: Record<string, string[]> = {};
  for (const c of COLS) {
    const v = split(d.terms[c.locale] ?? '');
    if (v.length) terms[c.locale] = v;
  }
  return { terms, keepOriginal: d.keepOriginal, note: d.note.trim() || null };
}

function fromEntry(e: GlossaryEntryDTO): Draft {
  return { terms: Object.fromEntries(COLS.map((c) => [c.locale, join(e.terms[c.locale])])), note: e.note ?? '', keepOriginal: e.keepOriginal };
}

/** Rows copied from Excel/Numbers/Sheets arrive tab-separated: Svenska, 简体中文, English, note, keep. */
export function parsePasted(text: string): Array<ReturnType<typeof toBody>> {
  const out: Array<ReturnType<typeof toBody>> = [];
  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const cells = line.split('\t');
    const [sv = '', zh = '', en = '', note = '', keep = ''] = cells.map((c) => c.trim());
    if (/^svenska$/i.test(sv)) continue; // header row
    const d: Draft = { terms: { 'sv-SE': sv, 'zh-Hans': zh, en }, note, keepOriginal: /^(x|ja|yes|1|true)$/i.test(keep) };
    const body = toBody(d);
    if (Object.keys(body.terms).length) out.push(body);
  }
  return out;
}

function DraftRow({ draft, onChange }: { draft: Draft; onChange: (d: Draft) => void }) {
  const t = useT();
  return (
    <>
      {COLS.map((c) => (
        <td key={c.locale}>
          <input
            value={draft.terms[c.locale] ?? ''}
            aria-label={c.label}
            placeholder={c.label}
            lang={c.locale}
            onChange={(e) => onChange({ ...draft, terms: { ...draft.terms, [c.locale]: e.target.value } })}
          />
        </td>
      ))}
      <td>
        <input value={draft.note} aria-label={t('glossaryNote')} placeholder={t('glossaryNote')} onChange={(e) => onChange({ ...draft, note: e.target.value })} />
        <label className="check small">
          <input type="checkbox" checked={draft.keepOriginal} onChange={(e) => onChange({ ...draft, keepOriginal: e.target.checked })} />
          <span>{t('keepOriginal')}</span>
        </label>
      </td>
    </>
  );
}

export function GlossaryTab() {
  const t = useT();
  const [entries, setEntries] = useState<GlossaryEntryDTO[]>([]);
  const [draft, setDraft] = useState<Draft>(empty());
  const [editing, setEditing] = useState<{ id: string; draft: Draft } | null>(null);
  const [filter, setFilter] = useState('');
  const [paste, setPaste] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => setEntries(await api<GlossaryEntryDTO[]>('GET', '/api/glossary')), []);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setMessage(null);
    try {
      await fn();
      await refresh();
      if (ok) setMessage(ok);
    } catch (err) {
      setMessage(t('error_generic', { code: err instanceof ApiError ? err.code : 'error' }));
    }
  };

  const add = (e: FormEvent) => {
    e.preventDefault();
    const body = toBody(draft);
    if (!Object.keys(body.terms).length) return;
    void run(async () => {
      await api('POST', '/api/glossary', body);
      setDraft(empty());
    });
  };

  const importRows = (rows: Array<ReturnType<typeof toBody>>) =>
    run(async () => {
      const r = await api<{ added: number; skipped: number }>('POST', '/api/glossary/import', { entries: rows });
      setMessage(t('glossaryImported', { added: r.added, skipped: r.skipped }));
      setPaste(null);
    });

  /**
   * Add the suggested list. Suggestions that were added earlier and never edited (still marked as a
   * suggestion) are updated in place, matched on their English term; entries you changed are left alone.
   */
  const applyStarter = () =>
    run(async () => {
      const key = (terms: Partial<Record<string, string[]>>) => (terms.en?.[0] ?? Object.values(terms).flat()[0] ?? '').toLowerCase();
      const byKey = new Map(entries.map((e) => [key(e.terms), e]));
      const toAdd: typeof GLOSSARY_STARTER = [];
      let updated = 0;
      for (const row of GLOSSARY_STARTER) {
        const existing = byKey.get(key(row.terms));
        if (!existing) toAdd.push(row);
        else if ((existing.note ?? '').startsWith(STARTER_MARK) && JSON.stringify(fromEntry(existing)) !== JSON.stringify(fromEntry({ ...existing, ...row, note: row.note }))) {
          await api('PUT', `/api/glossary/${existing.id}`, row);
          updated++;
        }
      }
      const r = toAdd.length ? await api<{ added: number; skipped: number }>('POST', '/api/glossary/import', { entries: toAdd }) : { added: 0 };
      setMessage(t('glossaryStarterDone', { added: r.added, updated }));
    });

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return entries;
    return entries.filter((e) => [...Object.values(e.terms).flat(), e.note ?? ''].some((x) => x?.toLowerCase().includes(f)));
  }, [entries, filter]);

  return (
    <div className="stack glossary">
      <p className="muted small">{t('glossaryIntro')}</p>

      <form onSubmit={add} className="glossary-add">
        <table className="table glossary-table">
          <tbody>
            <tr>
              <DraftRow draft={draft} onChange={setDraft} />
              <td className="actions">
                <button className="btn primary small" disabled={!Object.keys(toBody(draft).terms).length}>{t('add')}</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p className="muted small">{t('glossarySynonymsHint')}</p>
      </form>

      <div className="row wrap">
        <input type="search" placeholder={t('glossarySearch')} aria-label={t('glossarySearch')} value={filter} onChange={(e) => setFilter(e.target.value)} />
        <button className="btn small" onClick={() => setPaste(paste === null ? '' : null)}>{t('glossaryPaste')}</button>
        <button className="btn small" onClick={() => void applyStarter()}>{t('glossaryStarter')}</button>
      </div>

      {paste !== null && (
        <div className="stack">
          <textarea rows={6} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={'Svenska\t简体中文\tEnglish\tAnteckning\tÖversätt inte (x)'} aria-label={t('glossaryPaste')} />
          <p className="muted small">{t('glossaryPasteHint')}</p>
          <div className="row">
            <button className="btn primary small" disabled={!parsePasted(paste).length} onClick={() => void importRows(parsePasted(paste))}>
              {t('glossaryImport', { n: parsePasted(paste).length })}
            </button>
          </div>
        </div>
      )}

      {message && <p className="small" role="status">{message}</p>}

      <table className="table glossary-table">
        <thead>
          <tr>
            {COLS.map((c) => <th key={c.locale}>{c.label}</th>)}
            <th>{t('glossaryNote')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && (
            <tr>
              <td colSpan={5} className="muted">{t('glossaryEmpty')}</td>
            </tr>
          )}
          {shown.map((e) =>
            editing?.id === e.id ? (
              <tr key={e.id}>
                <DraftRow draft={editing.draft} onChange={(d) => setEditing({ id: e.id, draft: d })} />
                <td className="actions">
                  <button className="btn primary small" onClick={() => void run(async () => { await api('PUT', `/api/glossary/${e.id}`, toBody(editing.draft)); setEditing(null); })}>{t('save')}</button>
                  <button className="link-btn" onClick={() => setEditing(null)}>{t('cancel')}</button>
                </td>
              </tr>
            ) : (
              <tr key={e.id}>
                {COLS.map((c) => (
                  <td key={c.locale} lang={c.locale}>{join(e.terms[c.locale]) || <span className="muted">—</span>}</td>
                ))}
                <td>
                  {e.keepOriginal && <span className="tag">{t('keepOriginalShort')}</span>} <span className="muted small">{e.note}</span>
                </td>
                <td className="actions">
                  <button className="link-btn" onClick={() => setEditing({ id: e.id, draft: fromEntry(e) })}>{t('edit')}</button>
                  <button
                    className="link-btn danger"
                    onClick={() => {
                      if (confirm(t('glossaryConfirmDelete'))) void run(() => api('DELETE', `/api/glossary/${e.id}`));
                    }}
                  >
                    {t('delete')}
                  </button>
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}
